import {
   HealthcareFacilityLocation,
   HealthcareFacilityLocationQuery,
   HealthcareFacilityUnit,
   HealthcareFacilityUnitQuery,
   MedicalField,
} from '@dreryk/egabinet-healthcare-provider-frontend-api';
import { VisitLocation } from '@dreryk/egabinet-visit-frontend-api';

import { performCqrsQueryGet } from '@dreryk/command-query-react-common';
import { Slot } from '@dreryk/watermelon';
import { SelectFilterCreator } from '@dreryk/react-components';

import { assertUnreachable } from '~libs.typescript';

const isFacilityIdDefined = (facilityId: number | undefined): facilityId is number => {
   return typeof facilityId === 'number';
};

type VisitLocationVisitor<RETURN> = {
   visitFacilityLocation: (locationId: number) => RETURN;
   visitFacilityUnit: (unitId: number) => RETURN;
};

const createFacilityQueryVisitor = <RETURN>(
   visitLocation: VisitLocation,
   visitor: VisitLocationVisitor<RETURN>
): RETURN => {
   if (isFacilityIdDefined(visitLocation.healthcareFacilityLocationId)) {
      return visitor.visitFacilityLocation(visitLocation.healthcareFacilityLocationId);
   }
   if (isFacilityIdDefined(visitLocation.healthcareFacilityUnitId)) {
      return visitor.visitFacilityUnit(visitLocation.healthcareFacilityUnitId);
   }
   return assertUnreachable(visitLocation.healthcareFacilityLocationId || visitLocation.healthcareFacilityUnitId);
};

export const createMedicalDepartmentsSearchCreator = (
   visitLocation: VisitLocation
): (() => () => Promise<MedicalField[]>) => {
   return () => async () => {
      const currentVisitLocation = await performCqrsQueryGet<HealthcareFacilityLocation | HealthcareFacilityUnit>(
         () => {
            return createFacilityQueryVisitor<HealthcareFacilityLocationQuery | HealthcareFacilityUnitQuery>(
               visitLocation,
               {
                  visitFacilityLocation: (facilityLocationId) => {
                     const facilityLocationQuery = new HealthcareFacilityLocationQuery();
                     facilityLocationQuery.id = Slot.of(facilityLocationId);
                     return facilityLocationQuery;
                  },
                  visitFacilityUnit: (facilityUnitId) => {
                     const facilityUnitQuery = new HealthcareFacilityUnitQuery();
                     facilityUnitQuery.id = Slot.of(facilityUnitId);
                     return facilityUnitQuery;
                  },
               }
            );
         }
      );
      return (
         currentVisitLocation.scopeOfProvidedServicesList?.reduce((acc, scope) => {
            if (scope.medicalField && !acc.some((medicalField) => medicalField.code === scope.medicalField?.code)) {
               acc.push(scope.medicalField);
               return acc;
            }
            return acc;
         }, [] as MedicalField[]) ?? []
      );
   };
};

export const triggerLabelExtractor = (medicalField: MedicalField): string => {
   return `${medicalField.code} ${medicalField.name}`;
};

export const medicalDepartmentFilterCreator: SelectFilterCreator<MedicalField> = ({ includes }) => (medicalField) => {
   return includes(medicalField.code) || includes(medicalField.name);
};
